import { useLocation, useNavigate, useRouter } from '@tanstack/react-router'
import { usePortalTabsStore } from './store'
import { type PortalTab, type PortalTabType } from './tabState'

function routeToNavigateArgs(tab: PortalTab) {
    switch (tab.type) {
        case 'class':
            return { to: tab.route as never }
        case 'staff':
            return { to: tab.route as never }
        case 'student':
        default:
            return { to: tab.route as never }
    }
}

export function usePortalTabs() {
    const portalTabs = usePortalTabsStore()
    const navigate = useNavigate()
    const router = useRouter()
    const location = useLocation()
    const currentRoute = `${location.pathname}${location.searchStr}${location.hash}`
    const preferredStudentRoute = portalTabs.newStudentTabRoute === 'last' ? undefined : portalTabs.newStudentTabRoute
    const routeForNewStudent = (studentNo: string) => portalTabs.tabs.some((tab) => tab.type === 'student' && tab.resourceId === studentNo)
        ? undefined
        : preferredStudentRoute

    const goToTab = (tab: PortalTab | undefined) => {
        if (!tab || tab.route === currentRoute) {
            return
        }

        void navigate(routeToNavigateArgs(tab))
    }

    const selectTab = (tabId: string) => {
        const tab = portalTabs.tabs.find((item) => item.id === tabId)
        portalTabs.selectTab(tabId)
        goToTab(tab)
    }

    const closeAndNavigate = (action: () => void, closingCurrentTab: boolean) => {
        action()
        if (!closingCurrentTab) {
            return
        }

        queueMicrotask(() => {
            const state = usePortalTabsStore.getState()
            const selectedTab = state.tabs.find((tab) => tab.id === state.selectedTabId)
            if (selectedTab) {
                router.history.push(selectedTab.route)
                return
            }

            void navigate({ to: '/tabs' } as never)
        })
    }

    return {
        ...portalTabs,
        currentRoute,
        visibleTabs: portalTabs.tabs.filter((tab) => !portalTabs.hiddenTypes.includes(tab.type)),
        selectedTab: portalTabs.tabs.find((tab) => tab.id === portalTabs.selectedTabId),
        openResourceTab: (type: PortalTabType, resourceId: string, routePattern?: string) => {
            portalTabs.openTab(type, resourceId, routePattern || (type === 'student' ? routeForNewStudent(resourceId) : undefined))
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
        addResourceTab: (type: PortalTabType, resourceId: string) => portalTabs.addTab(type, resourceId, type === 'student' ? preferredStudentRoute : undefined),
        openResourceTabs: (type: PortalTabType, resourceIds: string[]) => {
            portalTabs.openTabs(type, resourceIds)
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
        selectTab,
        closeTab: (tabId: string) => {
            const tab = portalTabs.tabs.find((item) => item.id === tabId)
            closeAndNavigate(() => portalTabs.closeTab(tabId), tab?.route === currentRoute)
        },
        closeOtherTabs: (tabId: string, type?: PortalTabType) => {
            portalTabs.closeOtherTabs(tabId, type)
            selectTab(tabId)
        },
        closeTabsToLeft: (tabId: string, type?: PortalTabType) => {
            portalTabs.closeTabsToLeft(tabId, type)
            selectTab(tabId)
        },
        closeTabsToRight: (tabId: string, type?: PortalTabType) => {
            portalTabs.closeTabsToRight(tabId, type)
            selectTab(tabId)
        },
        closeTabsByType: (type?: PortalTabType) => {
            const selectedTab = portalTabs.tabs.find((tab) => tab.id === portalTabs.selectedTabId)
            const closingCurrentTab = selectedTab?.route === currentRoute && (!type || selectedTab.type === type)
            closeAndNavigate(() => portalTabs.closeTabsByType(type), closingCurrentTab)
        },
        selectPreviousTab: () => {
            portalTabs.selectPreviousTab()
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
        selectNextTab: () => {
            portalTabs.selectNextTab()
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
        openStudent: (studentNo: string) => {
            portalTabs.openTab('student', studentNo, routeForNewStudent(studentNo))
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
        addStudent: (studentNo: string) => portalTabs.addTab('student', studentNo, preferredStudentRoute),
        openStudents: (studentNos: string[]) => {
            portalTabs.openTabs('student', studentNos)
            queueMicrotask(() => {
                const state = usePortalTabsStore.getState()
                goToTab(state.tabs.find((tab) => tab.id === state.selectedTabId))
            })
        },
    }
}
